import { CssBaseline, ThemeProvider } from '@rossum/ui/material';
import { HTML_FONT_SIZE, resolveTheme } from '@rossum/ui/theme';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { mapValues } from 'remeda';
import { defaultTheme } from './constants/values';
import { safeOrganizationSelector } from './redux/modules/organization/selectors';
import { State } from './types/state';

type RuiThemeProviderProps = {
  children: React.ReactNode;
  theme?: 'dark' | 'light';
  isRoot?: boolean;
};

export const RuiThemeProvider = ({
  children,
  theme,
  isRoot = true,
}: RuiThemeProviderProps) => {
  const themeType = useSelector(
    (state: State) =>
      state.user.uiSettings.theme ??
      safeOrganizationSelector(state)?.uiSettings.theme ??
      defaultTheme
  );

  const branding = useSelector(safeOrganizationSelector)?.uiSettings?.branding;

  const themeConfig = useMemo(() => {
    const currentTheme = theme ?? (themeType === 'white' ? 'light' : 'dark');

    const resolvedTheme = resolveTheme(currentTheme, {
      // needs to be added to respect font-size: 10px on html
      typography: {
        htmlFontSize: HTML_FONT_SIZE,
        ...(branding?.font
          ? {
              fontFamily: branding?.font,
            }
          : {}),
      },
      // TODO: Document this and add value different than 0
      ...(branding?.borderRadiuses
        ? {
            shape: {
              borderRadius: 0,
            },
          }
        : {}),
      components: {
        MuiSelect: {
          styleOverrides: {
            select: {
              height: 0,
              lineHeight: 1.5,
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            // Fix for beamer snippets with white titles on white background.
            '.beamerAnnouncementSnippetContent': {
              backgroundColor: 'white',
            },
            '.beamerAnnouncementSnippetTitle, .beamerAnnouncementSnippetText': {
              color: 'black',
            },
            body: {
              lineHeight: 1.42857143,
              fontSize: '1.4rem',
              letterSpacing: 'unset',
              '*::-webkit-scrollbar': {
                width: 8,
                height: 8,
              },
            },
          },
        },
      },
    });

    return {
      ...resolvedTheme,
      // custom branding - need `augmentColor` to generate dark/light shades
      palette: branding?.colors?.primary
        ? {
            ...resolvedTheme.palette,
            primary: resolvedTheme.palette.augmentColor({
              color: { main: branding.colors.primary },
            }),
          }
        : resolvedTheme.palette,
      // Ensure all z-indexes are above the values used in our app
      // https://mui.com/customization/z-index/
      zIndex: mapValues(resolvedTheme.zIndex, zIndex => zIndex * 10),
    };
  }, [
    branding?.borderRadiuses,
    branding?.colors?.primary,
    branding?.font,
    theme,
    themeType,
  ]);

  useEffect(() => {
    if (isRoot) {
      const themePalette = themeConfig.palette;
      [
        // keep until we remove legacy variables.sass
        ['--color-primary', themePalette.primary.main],
        ['--color-primary-dark', themePalette.primary.dark],
        ['--color-primary-light', themePalette.primary.light],

        ['--color-success', themePalette.success.main],

        ['--color-error', themePalette.error.main],
        ['--color-error-dark', themePalette.error.dark],
        ['--color-error-light', themePalette.error.light],

        ['--color-warning', themePalette.warning.main],

        ['--text-primary', themePalette.text.primary],

        // YOLO: original brown-gray values were `themeConfig.palette.mode === 'dark' ? '#979797' : '#757575'`
        ['--text-secondary', themePalette.text.secondary],

        ['--bg-paper', themePalette.background.paper],
        ['--bg-default', themePalette.background.default],

        ['--divider', themePalette.divider],

        // for the sake of simplicity we stick to original values, too many occurences to check
        [
          '--charcoal-gray',
          themePalette.mode === 'dark'
            ? 'rgb(44, 43, 51)'
            : 'rgb(255, 255, 255)',
        ],
        [
          '--charcoal-gray-light',
          themePalette.mode === 'dark' ? '#403e46' : '#979797',
        ],
      ].forEach(([variable, value]) => {
        document.documentElement.style.setProperty(variable, value);
      });
    }
  }, [isRoot, themeConfig.palette, themeConfig.shape.borderRadius]);

  return (
    <ThemeProvider theme={themeConfig}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
